import React from 'react';
import { motion } from 'framer-motion';
import './css/BusinessVertical.css';
import SEO from '../components/SEO';
import { serviceSchema, productSchema } from '../components/StructuredData';

const AromaticChemicals = () => {
  const structuredData = [
    serviceSchema(
      "Aromatic Chemicals & Derivatives Manufacturing",
      "Leading manufacturer of specialty aromatic chemicals including Iso Amyl Alcohol and natural esters for fragrance, flavor, and pharmaceutical industries.",
      "Chemical Manufacturing"
    ),
    productSchema(
      "Iso Amyl Alcohol",
      "High-purity aromatic alcohol used in fragrance and flavor applications",
      "Aromatic Chemicals"
    )
  ];

  return (
    <>
      <SEO
        title="Aromatic Chemicals & Derivatives - Nimble Group Manufacturing"
        description="Leading manufacturer of Iso Amyl Alcohol and specialty aromatic chemicals for fragrance, flavor, and pharmaceutical industries. 25+ years of excellence with 99.8% purity standards."
        keywords="aromatic chemicals, iso amyl alcohol, fragrance chemicals, flavor ingredients, pharmaceutical chemicals, specialty chemicals, chemical manufacturing"
        canonical="/business/aromatic-chemicals"
        structuredData={structuredData}
      />
      <AromaticChemicalsContent />
    </>
  );
};

const AromaticChemicalsContent = () => {
  const fadeIn = {
    initial: { opacity: 0, y: 20 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.6 }
  };

  // Key stats with animation
  const stats = [
    { value: '25+', label: 'Years of Excellence' },
    { value: '99.8%', label: 'Product Purity' },
    { value: '50+', label: 'Global Partners' },
    { value: '30+', label: 'Product Variants' }
  ];

  return (
    <div className="business-vertical-page">
      <motion.div 
        className="business-vertical-hero aromatic-chemicals"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.8 }}
      >
        <div className="container">
          <h1>Aromatic Chemicals & Derivatives</h1>
          <h2 className="tagline">"Crafting natural molecules for fragrances, flavors, and wellness."</h2>
        </div>
      </motion.div>

      <section className="business-vertical-content">
        <div className="container">
          <motion.div
            className="stats-container"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.8, delay: 0.2 }}
          >
            {stats.map((stat, index) => (
              <motion.div 
                className="stat-box"
                key={stat.label}
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.5, delay: 0.3 + (index * 0.1) }}
              >
                <div className="stat-value">{stat.value}</div>
                <div className="stat-label">{stat.label}</div>
              </motion.div>
            ))}
          </motion.div>

          <motion.div
            className="content-section"
            initial={fadeIn.initial}
            animate={fadeIn.animate}
            transition={fadeIn.transition}
          >
            <p>For over 25 years, our Specialty Chemicals Strategic Business Unit (SBU) has been at the forefront of manufacturing high-performance specialty chemicals catering to the exacting demands of the fragrance, flavors, and allied industries. With a consistent emphasis on quality, reliability, and sustainable sourcing, this division has built a trusted name as one of the largest and most dependable manufacturers of Iso Amyl Alcohol derivatives and esters.</p>
            <p>We are one of India's leading manufacturers of Iso Amyl Alcohol, its derivatives & and various natural esters. These eco-conscious aromatic compounds find widespread use in the fragrance, flavors, bulk drug and pharmaceutical industries. Derived from renewable sources, our aromatics ensure performance, purity, and environmental responsibility.</p>
          </motion.div>

          <motion.div
            className="content-section"
            initial={fadeIn.initial}
            animate={fadeIn.animate}
            transition={{ ...fadeIn.transition, delay: 0.1 }}
          >
            <h3>Core Competency</h3>
            <div className="section-with-icon">
              <div className="section-icon">
                <span className="material-icon">🧪</span>
              </div>
              <div className="section-content">
                <p>At the heart of our product portfolio lies Iso Amyl Alcohol, a naturally derived intermediate sourced from sugarcane distillery operations. Leveraging this renewable raw material, our SBU specializes in the manufacture of a wide range of high-purity esters and derivatives, serving as critical building blocks for:</p>
                <ul>
                  <li>Fragrance and Flavors</li>
                  <li>Bulk Drugs & Pharmaceuticals</li>
                  <li>Metalworking and Lubricants</li>
                  <li>Industrial Cleaners and Degreasers</li>
                </ul>
                <p>Our expertise in fractional distillation, esterification, and specialized chemical synthesis allows us to offer products that meet the stringent purity and olfactory performance standards demanded by global fragrance and flavor houses.</p>
              </div>
            </div>
          </motion.div>

          <motion.div
            className="content-section"
            initial={fadeIn.initial}
            animate={fadeIn.animate}
            transition={{ ...fadeIn.transition, delay: 0.2 }}
          >
            <h3>Product Range</h3>
            <div className="section-with-icon">
              <div className="section-icon">
                <span className="material-icon">🔬</span>
              </div>
              <div className="section-content">
                <p>Our comprehensive product lineup includes, but is not limited to:</p>
                <ul>
                  <li>Iso Amyl Alcohol (99.8% purity)</li>
                  <li>CAMP</li>
                  <li>Amyl Acetate</li>
                  <li>Amyl Valerate</li>
                  <li>Amyl Salicylate</li>
                  <li>Amyl Laurate</li>
                  <li>Amyl Chloride</li>
                  <li>Amyl Propionate</li>
                </ul>
                <p>These specialty esters play a vital role as solvents, fixatives, and fragrance carriers, ensuring consistent performance across complex formulations. Additionally, many of these derivatives find diverse applications in metalworking fluids, industrial lubricants, cleaning formulations, and pharmaceutical intermediates.</p>
              </div>
            </div>
          </motion.div>

          <motion.div
            className="content-section"
            initial={fadeIn.initial}
            animate={fadeIn.animate}
            transition={{ ...fadeIn.transition, delay: 0.3 }}
          >
            <h3>Leadership in Manufacturing</h3>
            <div className="section-with-icon">
              <div className="section-icon">
                <span className="material-icon">🏭</span>
              </div>
              <div className="section-content">
                <p>We take pride in being one of the largest manufacturers of CAMP and Amyl Laurate in the region, supported by state-of-the-art manufacturing facilities and robust quality assurance protocols. Our advanced process control systems ensure batch-to-batch consistency, traceability, and compliance with international purity benchmarks.</p>
                <p>The exceptional purity of our Iso Amyl Alcohol (99.4%) distinctly positions us as a preferred supplier to fragrance and flavor manufacturers, where precision in olfactory profile and formulation stability is non-negotiable.</p>
              </div>
            </div>
          </motion.div>

          <motion.div
            className="content-section"
            initial={fadeIn.initial}
            animate={fadeIn.animate}
            transition={{ ...fadeIn.transition, delay: 0.4 }}
          >
            <h3>Commitment to Quality and Sustainability</h3>
            <div className="section-with-icon">
              <div className="section-icon">
                <span className="material-icon">🌱</span>
              </div>
              <div className="section-content">
                <p>Quality is intrinsic to our operations. Every product is subjected to rigorous analytical and performance testing, ensuring it aligns with the unique sensory and technical requirements of each application. By emphasizing sustainable raw material sourcing and responsible manufacturing practices, our SBU contributes to reducing the environmental footprint of our products while delivering unmatched value to our customers.</p>
              </div>
            </div>
          </motion.div>

          <motion.div
            className="content-section"
            initial={fadeIn.initial}
            animate={fadeIn.animate}
            transition={{ ...fadeIn.transition, delay: 0.5 }}
          >
            <h3>Future-Ready and Customer-Focused</h3>
            <div className="section-with-icon">
              <div className="section-icon">
                <span className="material-icon">🚀</span>
              </div>
              <div className="section-content">
                <p>Backed by decades of technical expertise and customer partnerships, our Specialty Chemicals Division continues to innovate and expand its capabilities to support evolving market needs. Whether for high-end fragrance compositions, pharmaceutical intermediates, or performance chemicals for industrial applications, our SBU remains committed to delivering quality-driven, reliable, and sustainable chemical solutions.</p>
              </div>
            </div>
          </motion.div>

          <motion.div 
            className="cta-section"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.6 }}
          >
            <h3>Ready to Experience Our Aromatic Excellence?</h3>
            <p>Connect with our experts to discuss how our specialty chemicals can enhance your products.</p>
            <button className="cta-button">Contact Our Team</button>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default AromaticChemicals; 